import type { Fixture, Prediction } from '@h2h/shared';
import { Check, Lock } from '../../../components/ui/icons';
import { PickPointsBadge } from '../../../components/play/PickPointsBadge';
import { timeLabel } from '../helpers';
import { teamCode, shortTeam } from '../../../lib/teams';
import { cn } from '../../../lib/cn';
import { SLATE_DARK_SHELL } from './constants';
import { CountdownChip } from './CountdownChip';
import { OverlappingTeamBadges } from './OverlappingTeamBadges';
import { inlinePickLabel, pickOutcome } from './pickHelpers';

/** One slate row in the picks card — overlapping flags, per-fixture countdown, inline pick. */
export function SlateRow({
  fixture,
  nationalTeams = false,
  prediction,
  now,
  isUrgent,
  preview,
  onOpen
}: {
  fixture: Fixture;
  nationalTeams?: boolean;
  prediction: Prediction | undefined;
  now: number;
  isUrgent: boolean;
  preview: boolean;
  onOpen: () => void;
}) {
  const homeCode = teamCode(fixture.homeTeam, fixture.homeCode);
  const awayCode = teamCode(fixture.awayTeam, fixture.awayCode);
  const isPicked = !!prediction;
  // Once a picked fixture is under way, surface its real result + points right here
  // so the locked card reads like a live scoreboard (no tap, no "Watch live").
  const outcome = prediction && !preview ? pickOutcome(prediction, fixture) : null;
  const perfect = outcome?.perfect ?? false;
  const kickedOff = fixture.status !== 'SCHEDULED' || fixture.kickoffAt <= now;
  const dark = isUrgent && !isPicked && !kickedOff && !preview;
  const meta = fixture.venue ?? '';

  const isLiveFixture = fixture.status === 'LIVE';
  const isSettledFixture = fixture.status === 'SETTLED' || fixture.status === 'VOID';

  const shell = preview
    ? 'border-black/8 bg-fill/40 opacity-90'
    : perfect
      ? 'border-[rgba(36,197,106,0.45)] bg-[linear-gradient(135deg,rgba(46,219,123,0.16),rgba(11,160,120,0.07))] shadow-[0_14px_28px_-16px_rgba(11,160,120,0.65)]'
      : isPicked && isLiveFixture
        ? 'border-[rgba(239,65,53,0.22)] bg-[rgba(239,65,53,0.04)]'
        : isPicked
          ? 'border-[rgba(36,197,106,0.28)] bg-[rgba(36,197,106,0.08)]'
          : dark
            ? SLATE_DARK_SHELL
            : isLiveFixture && !isPicked
              ? 'border-[rgba(239,65,53,0.15)] bg-[rgba(239,65,53,0.03)]'
              : kickedOff
                ? 'border-black/8 bg-fill/60'
                : 'border-black/8 bg-white';

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'tap flex w-full items-center gap-[11px] rounded-[16px] border px-[13px] py-3 text-left transition-[background,border-color,transform] duration-150 active:scale-[0.985]',
        shell
      )}
    >
      <OverlappingTeamBadges
        homeCode={homeCode}
        awayCode={awayCode}
        homeLogo={fixture.homeLogo}
        awayLogo={fixture.awayLogo}
        homeTeam={fixture.homeTeam}
        awayTeam={fixture.awayTeam}
        flag={nationalTeams}
      />
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            'truncate text-[14px] font-extrabold tracking-[-0.015em]',
            dark ? 'text-white' : 'text-ink'
          )}
        >
          {shortTeam(fixture.homeTeam)}{' '}
          <span className={cn('font-semibold', dark ? 'text-white/40' : 'text-[#C7C7CF]')}>v</span>{' '}
          {shortTeam(fixture.awayTeam)}
        </div>
        {meta ? (
          <div className={cn('mt-[3px] text-[11px] font-semibold', dark ? 'text-white/50' : 'text-[#9A9AA4]')}>
            {meta}
          </div>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        {outcome ? (
          <span className="num inline-flex items-center gap-[5px] text-[14px] font-extrabold tracking-[-0.01em] text-ink">
            {outcome.live && <span className="h-[6px] w-[6px] animate-h2h-pulse rounded-full bg-accent" />}
            {outcome.score}
            <span className={cn('text-[10px] font-bold tracking-wide', outcome.live ? 'text-accent' : 'text-ink3')}>
              {outcome.live ? outcome.clock : 'FT'}
            </span>
          </span>
        ) : (
          <span
            className={cn(
              'num text-[14px] font-extrabold tracking-[-0.01em]',
              dark ? 'text-white' : 'text-ink'
            )}
          >
            {timeLabel(fixture.kickoffAt)}
          </span>
        )}
        {preview ? (
          <span className="inline-flex items-center gap-1 rounded-[8px] bg-fill px-2 py-1 text-[11.5px] font-extrabold text-ink3">
            <Lock width={10} height={10} strokeWidth={2.6} />
            Locked
          </span>
        ) : outcome ? (
          <PickPointsBadge outcome={outcome} />
        ) : isPicked ? (
          <span className="inline-flex items-center gap-1.5 rounded-[9px] bg-[rgba(36,197,106,0.14)] py-[5px] pl-1.5 pr-[9px] text-[11.5px] font-extrabold text-accent-ink">
            <span className="grid h-[15px] w-[15px] shrink-0 place-items-center rounded-full bg-accent">
              <Check width={9} height={9} strokeWidth={4} className="text-white" />
            </span>
            <span className="num tracking-[-0.01em]">{inlinePickLabel(prediction, homeCode, awayCode)}</span>
          </span>
        ) : isLiveFixture ? (
          <span className="inline-flex items-center gap-[5px] rounded-[8px] bg-[rgba(239,65,53,0.10)] px-2 py-1 text-[11.5px] font-extrabold text-[#C0392B]">
            <span className="h-[5px] w-[5px] shrink-0 animate-h2h-pulse rounded-full bg-[#EF4135]" />
            Live
          </span>
        ) : isSettledFixture ? (
          <span className="inline-flex items-center gap-1 rounded-[8px] bg-[#EDEDF0] px-2 py-1 text-[11.5px] font-extrabold text-ink3">
            FT
          </span>
        ) : kickedOff ? (
          <span className="inline-flex items-center gap-1 rounded-[8px] bg-fill px-2 py-1 text-[11.5px] font-extrabold text-ink3">
            <Lock width={10} height={10} strokeWidth={2.6} />
            Locked
          </span>
        ) : (
          <CountdownChip kickoffAt={fixture.kickoffAt} now={now} urgent={dark} />
        )}
      </div>
    </button>
  );
}
